/* ───────── Remote kiosk config (sourced from Till attributes via GraphQL) ───────── */

// Internal structure of the pong.categoryConfigs JSON blob
export interface CategoryConfigEntry {
  product_layout?: string
  icon?: string
  image_url?: string
  visible?: boolean
}

/** Tema-preset — applicerar en uppsättning fördefinierade färger.
 *  'custom' = användaren har överridat färger individuellt (preset används bara
 *  i backoffice-editor som startpunkt). */
export type ThemePreset = 'pong-dark' | 'pong-light' | 'kebabdudes-dark' | 'kebabdudes-light' | 'custom'

/** Brand key — styr PWA-manifest, default-ikoner och splash-bild för
 *  external customers (t.ex. Kebabdudes). 'pong' är default. */
export type BrandKey = 'pong' | 'kebabdudes' | 'custom'

// All pong.* attributes stored on the Till, parsed into typed fields
export interface KioskRemoteConfig {
  // Theme
  colorAccent?: string
  colorAccentHover?: string
  colorBg?: string
  colorSurface?: string
  colorSurface2?: string
  colorText?: string
  colorMuted?: string
  /** Vald tema-preset. Backoffice fyller i färgfälten ovanifrån preset-tabellen,
   *  men kiosken läser ALLTID color*-fälten direkt — preset-namnet är bara
   *  metadata för UI:t. */
  themePreset?: ThemePreset

  // Branding
  videoUrlMobile?: string | null
  videoUrlDesktop?: string | null
  logoUrl?: string | null
  /** Kioskens visningsnamn (titel på startskärmen, manifest-name). */
  appName?: string
  pageTitle?: string
  /** PWA-konfig — påverkar "Lägg till på hemskärmen" på iOS/Android.
   *  Manifest:en byggs dynamiskt vid load baserat på dessa fält så varje
   *  kund kan ha egen ikon + namn på iPaden utan deploy. */
  brandKey?: BrandKey
  appIconUrl192?: string | null
  appIconUrl512?: string | null
  /** Themecolor i manifest (status bar / splash background på iOS).
   *  Default = colorAccent. */
  pwaThemeColor?: string

  /** Bildspel/media-lista för startskärmen — används OM den har items.
   *  Annars faller vi tillbaka till videoUrl* (singel video).
   *  Itemtyp auto-detekteras från URL-extension om type=undefined. */
  startScreenMedia?: StartScreenMedia[]

  // Orientation
  layoutMode?: 'portrait' | 'landscape'

  // Layout
  categoryNavStyle?: string
  defaultProductLayout?: string
  categoryColumns?: number // 2 or 3 columns for category grid
  /** Hoppa över kategorigrid:en — kunden landar direkt på "HELA MENYN"-vyn
   *  efter Äta här/Ta med-valet. Användbart för restauranger med få kategorier
   *  (Kebabdudes etc.) där grid:en bara är extra klick. */
  skipCategoryGrid?: boolean
  /** Visa sticky kategoribar i HELA MENYN — klick på en kategori smooth-
   *  scrollar till sektionen, aktiv kategori highlightas medan kund scrollar.
   *  Bara meningsfullt när skipCategoryGrid är på (i vanligt läge har user
   *  redan kategorigrid:en som navigation). */
  fullMenuCategoryBar?: boolean
  /** Justering av kategoribarens innehåll. 'center' centrerar knapparna
   *  när de får plats; vid overflow scrollar de horisontellt från vänster.
   *  'left' (default) vänsterställer alltid. */
  fullMenuCategoryBarAlign?: 'left' | 'center'
  categoryOrder?: string[]
  categoryConfigs?: Record<string, CategoryConfigEntry>

  // Legacy
  threeColCategories?: string[]
  categoryIcons?: Record<string, string>

  // Collapse
  collapseEnabled?: boolean
  maxCollapsedRows?: number

  // Timeouts
  inactivityTimeout?: number
  countdownDuration?: number
  receiptAutoDismiss?: number

  // Tips
  tipsEnabled?: boolean
  tipPercentages?: number[]

  // Language
  defaultLocale?: string
  availableLocales?: string[]

  // Menu types
  menuTypeOptions?: string[]

  // Filler tiles (fills empty grid slots)
  fillerTiles?: FillerTilesConfig
}

export interface FillerTilesConfig {
  enabled: boolean
  brand: { enabled: boolean; tagline: string }
  promos: { enabled: boolean; items: FillerPromo[] }
  category_tips: { enabled: boolean; items: FillerTip[] }
  // Kategorisidans fyllnadsruta — en dedikerad bild/video-banner på
  // kategorisidan som spänner över `span_cols` kolumner. Placeras efter
  // sista kategorin; CSS-grid wrap:ar automatiskt till ny rad om den
  // inte får plats i sista partiella raden.
  category_page?: FillerCategoryPage
}

export interface FillerPromo {
  image_url: string
  headline: string
  subtext: string
}

export interface FillerTip {
  category: string
  image_url: string
  headline: string
  subtext: string
}

/** En slide i startskärmens bildspel. */
export interface StartScreenMedia {
  url: string
  /** 'image' | 'video' — om undefined gissar vi från URL-extension. */
  type?: 'image' | 'video'
  /** Visningstid i sekunder (image-slides). Default 5s. För video används
   *  videons egen längd om duration är 0/undefined; annars klipps videon. */
  duration_sec?: number
}

export interface FillerCategoryPage {
  enabled: boolean
  image_url: string // bild eller video (.mp4/.webm/.mov/.m4v)
  headline: string // valfri
  subtext: string // valfri
  span_cols: number // 1-4, hur många grid-kolumner rutan spänner över
}

// Fallback defaults used when the till hasn't configured fillerTiles yet —
// so even a fresh install shows nice fill tiles instead of empty gaps.
export const DEFAULT_FILLER_TILES: FillerTilesConfig = {
  enabled: true,
  brand: { enabled: true, tagline: 'Asiatisk buffé sedan 1997' },
  promos: {
    enabled: true,
    items: [
      { image_url: '', headline: 'HAPPY HOUR', subtext: 'Vardagar 15–17 · −20% på drycker 🍺' },
      { image_url: '', headline: 'STUDENTRABATT', subtext: 'Visa CSN-kort för 10% rabatt 📚' },
      {
        image_url: '',
        headline: 'PONG APP',
        subtext: 'Ladda ner vår app — snabbare beställning 📱',
      },
    ],
  },
  category_tips: {
    enabled: true,
    items: [
      {
        category: 'DESSERT',
        image_url: '',
        headline: 'Glöm inte desserten!',
        subtext: 'Mochi, pannacotta & mer 🍨',
      },
      {
        category: 'DRYCKER',
        image_url: '',
        headline: 'Törstig?',
        subtext: 'Kolla in vårt dryckessortiment 🧋',
      },
      {
        category: 'ÖL & SAKE',
        image_url: '',
        headline: 'Helgstämning?',
        subtext: 'Öl & sake till maten 🍻',
      },
    ],
  },
  category_page: {
    enabled: false,
    image_url: '',
    headline: '',
    subtext: '',
    span_cols: 2,
  },
}

// ── Coercion helpers ──────────────────────────────────────────────────────────

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

function bool(v: unknown): boolean | undefined {
  if (v === 'true') return true
  if (v === 'false') return false
  return undefined
}

function num(v: unknown): number | undefined {
  if (typeof v !== 'string' || v.length === 0) return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

function json<T>(v: unknown): T | undefined {
  if (typeof v !== 'string' || v.length === 0) return undefined
  try {
    return JSON.parse(v) as T
  } catch {
    return undefined
  }
}

// ── Parser ────────────────────────────────────────────────────────────────────

/**
 * Extracts all pong.* attribute values from the raw GraphQL Till object and
 * returns a typed KioskRemoteConfig.  Every attribute arrives as a string (or
 * null) from the API, so we coerce each field to its proper type here.
 */
export function parseTillAttributes(till: Record<string, unknown>): KioskRemoteConfig {
  return {
    // Theme
    colorAccent: str(till.colorAccent),
    colorAccentHover: str(till.colorAccentHover),
    colorBg: str(till.colorBg),
    colorSurface: str(till.colorSurface),
    colorSurface2: str(till.colorSurface2),
    colorText: str(till.colorText),
    colorMuted: str(till.colorMuted),

    themePreset: str(till.themePreset) as ThemePreset | undefined,

    // Branding
    videoUrlMobile: str(till.videoUrlMobile) ?? null,
    videoUrlDesktop: str(till.videoUrlDesktop) ?? null,
    logoUrl: str(till.logoUrl) ?? null,
    appName: str(till.appName),
    pageTitle: str(till.pageTitle),
    brandKey: str(till.brandKey) as BrandKey | undefined,
    appIconUrl192: str(till.appIconUrl192) ?? null,
    appIconUrl512: str(till.appIconUrl512) ?? null,
    pwaThemeColor: str(till.pwaThemeColor),
    startScreenMedia: json<StartScreenMedia[]>(till.startScreenMedia) ?? [],

    // Orientation
    layoutMode: till.layoutMode === 'landscape' ? 'landscape' : 'portrait',

    // Layout
    categoryNavStyle: str(till.categoryNavStyle),
    defaultProductLayout: str(till.defaultProductLayout),
    categoryColumns: num(till.categoryColumns),
    skipCategoryGrid: bool(till.skipCategoryGrid),
    fullMenuCategoryBar: bool(till.fullMenuCategoryBar),
    fullMenuCategoryBarAlign: str(till.fullMenuCategoryBarAlign) === 'center' ? 'center' : 'left',
    categoryOrder: json<string[]>(till.categoryOrder) ?? [],
    categoryConfigs: json<Record<string, CategoryConfigEntry>>(till.categoryConfigs),

    // Legacy
    threeColCategories: json<string[]>(till.threeColCategories),
    categoryIcons: json<Record<string, string>>(till.categoryIcons),

    // Collapse
    collapseEnabled: bool(till.collapseEnabled),
    maxCollapsedRows: num(till.maxCollapsedRows),

    // Timeouts
    inactivityTimeout: num(till.inactivityTimeout),
    countdownDuration: num(till.countdownDuration),
    receiptAutoDismiss: num(till.receiptAutoDismiss),

    // Tips
    tipsEnabled: bool(till.tipsEnabled),
    tipPercentages: json<number[]>(till.tipPercentages),

    // Language
    defaultLocale: str(till.defaultLocale),
    availableLocales: json<string[]>(till.availableLocales),

    // Menu types
    menuTypeOptions: json<string[]>(till.menuTypeOptions),

    // Filler tiles — falls back to the default seed so fresh kiosks still show fun content
    fillerTiles: json<FillerTilesConfig>(till.fillerTiles) ?? DEFAULT_FILLER_TILES,
  }
}

// ── Tema-presets ──────────────────────────────────────────────────────────────

/**
 * Färgvärden per preset. Backoffice använder dessa som startpunkt när user
 * väljer en preset i "Tema"-dropdown:en. Användaren kan sen overrida enskilda
 * färger via custom-flikar (vilket sätter themePreset='custom').
 *
 * Lägg till nya kunder här när de kommer in. Ljusa teman kräver att alla
 * komponenter respekterar CSS-variabler — ingen hardkodad text-white/bg-black.
 */
export const THEME_PRESETS: Record<
  Exclude<ThemePreset, 'custom'>,
  {
    label: string
    colors: {
      colorAccent: string
      colorAccentHover: string
      colorBg: string
      colorSurface: string
      colorSurface2: string
      colorText: string
      colorMuted: string
    }
    /** Föreslagen pwaThemeColor (matchar manifest theme_color) */
    pwaThemeColor: string
  }
> = {
  'pong-dark': {
    label: 'Pong (mörk)',
    colors: {
      colorAccent: '#D4872C',
      colorAccentHover: '#E8A84C',
      colorBg: '#0A0A0A',
      colorSurface: '#141414',
      colorSurface2: '#1E1E1E',
      colorText: '#F5F5F5',
      colorMuted: '#A0A0A0',
    },
    pwaThemeColor: '#D4872C',
  },
  'pong-light': {
    label: 'Pong (ljus)',
    colors: {
      colorAccent: '#D4872C',
      colorAccentHover: '#B86F1F',
      colorBg: '#FAFAF7',
      colorSurface: '#FFFFFF',
      colorSurface2: '#F0EFEA',
      colorText: '#1A1A1A',
      colorMuted: '#6B6B6B',
    },
    pwaThemeColor: '#D4872C',
  },
  'kebabdudes-dark': {
    label: 'Kebabdudes (mörk)',
    colors: {
      // Kebabdudes-röd — uppdatera om kunden specificerar exakt färg
      colorAccent: '#E63946',
      colorAccentHover: '#F25664',
      colorBg: '#0F0F10',
      colorSurface: '#1B1B1D',
      colorSurface2: '#262629',
      colorText: '#F8F9FA',
      colorMuted: '#9CA3AF',
    },
    pwaThemeColor: '#E63946',
  },
  'kebabdudes-light': {
    label: 'Kebabdudes (ljus)',
    colors: {
      colorAccent: '#E63946',
      colorAccentHover: '#C5303D',
      colorBg: '#FFFAF7',
      colorSurface: '#FFFFFF',
      colorSurface2: '#F4EDE8',
      colorText: '#1A1A1A',
      colorMuted: '#6B6B6B',
    },
    pwaThemeColor: '#E63946',
  },
}

export function getThemePreset(key: ThemePreset | undefined): (typeof THEME_PRESETS)[keyof typeof THEME_PRESETS] | null {
  if (!key || key === 'custom') return null
  return THEME_PRESETS[key] ?? null
}
